import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { analytics } from '@/lib/analytics';
import { logError, logDebug } from '@/utils/errorHandler';
import { useTranslation } from 'react-i18next';

interface TocItem {
  id: string;
  title: string;
  level: number;
}

interface TableOfContentsProps {
  className?: string;
  containerSelector?: string;
}

export function TableOfContents({ className, containerSelector = 'main' }: TableOfContentsProps) {
  const [toc, setToc] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    const generateToc = () => {
      // Add a small delay to ensure content is rendered
      setTimeout(() => {
        const container = document.querySelector(containerSelector);
        if (!container) {
          logError(
            'Container element not found for table of contents',
            {
              component: 'TableOfContents',
              action: 'generateToc',
              metadata: {
                containerSelector,
              },
            },
            {
              showToast: true,
              toastMessage: t('errors:ui.containerNotFound'),
              userFacing: true,
            }
          );
          return;
        }

        // Only get H1 and H2 headings for cleaner TOC
        const headings = container.querySelectorAll('h1, h2');
        logDebug('Table of contents headings found', {
          component: 'TableOfContents',
          action: 'generateToc',
          headingsCount: headings.length,
          containerSelector,
        });

        const tocItems: TocItem[] = [];

        headings.forEach(heading => {
          const level = parseInt(heading.tagName.charAt(1));
          const title = heading.textContent || '';
          let id = heading.id;

          // Generate ID if not present
          if (!id) {
            id = title
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/(^-|-$)/g, '');

            // Ensure uniqueness
            let counter = 1;
            let uniqueId = id;
            while (document.getElementById(uniqueId)) {
              uniqueId = `${id}-${counter}`;
              counter++;
            }

            heading.id = uniqueId;
            id = uniqueId;
          }

          tocItems.push({ id, title, level });
        });

        logDebug('Table of contents items generated', {
          component: 'TableOfContents',
          action: 'generateToc',
          tocItems,
        });
        setToc(tocItems);
      }, 100); // Small delay to ensure content is rendered
    };

    generateToc();

    // Set up MutationObserver to watch for content changes
    const container = document.querySelector(containerSelector);
    if (container) {
      const observer = new MutationObserver(mutations => {
        const hasNewContent = mutations.some(
          mutation => mutation.type === 'childList' && mutation.addedNodes.length > 0
        );
        if (hasNewContent) {
          generateToc();
        }
      });

      observer.observe(container, {
        childList: true,
        subtree: true,
      });

      return () => observer.disconnect();
    }
  }, [containerSelector, t]);

  useEffect(() => {
    // Scroll spy functionality
    const handleScroll = () => {
      const headings = toc.map(item => document.getElementById(item.id)).filter(Boolean);

      // Find the heading that's currently in view
      let current = '';

      for (const heading of headings) {
        if (heading) {
          const rect = heading.getBoundingClientRect();
          // Check if heading is in the upper half of the viewport
          if (rect.top <= window.innerHeight * 0.5 && rect.bottom >= 0) {
            current = heading.id;

            // Track section view analytics
            const tocItem = toc.find(item => item.id === heading.id);
            if (tocItem && activeId !== current) {
              analytics.trackSectionView(current, tocItem.title);
            }
          }
        }
      }

      setActiveId(current);
    };

    // Add scroll listener with throttling
    let timeoutId: NodeJS.Timeout;
    const throttledScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 100);
    };

    window.addEventListener('scroll', throttledScroll);
    handleScroll(); // Initial call

    return () => {
      window.removeEventListener('scroll', throttledScroll);
      clearTimeout(timeoutId);
    };
  }, [toc, activeId]);

  const scrollToHeading = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      // Track navigation
      const tocItem = toc.find(item => item.id === id);
      if (tocItem) {
        analytics.track('docs_toc_click', {
          section_id: id,
          section_title: tocItem.title,
          level: tocItem.level,
        });
      }
    }
  };

  if (toc.length === 0) {
    return <div className="text-sm text-muted-foreground">No headings found in this section.</div>;
  }

  return (
    <nav className={cn('space-y-1', className)} aria-label="Table of contents">
      <ul className="space-y-1">
        {toc.map(item => (
          <li key={item.id}>
            <button
              onClick={() => scrollToHeading(item.id)}
              className={cn(
                'block w-full text-left py-1 px-2 rounded-md transition-colors hover:bg-muted',
                'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                {
                  // Main sections (H1) - larger, bolder, more prominent
                  'text-sm font-semibold text-foreground pl-0': item.level === 1,
                  'text-primary bg-muted/50 font-semibold':
                    activeId === item.id && item.level === 1,

                  // Sub-sections (H2) - smaller, lighter, indented
                  'text-xs text-muted-foreground pl-4': item.level === 2,
                  'text-primary bg-muted/30 font-medium': activeId === item.id && item.level === 2,

                  // Hover states
                  'hover:text-foreground': item.level === 2 && activeId !== item.id,
                }
              )}
            >
              {item.title}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}
